import React, { useMemo, useRef } from 'react';
import { FormatOptions, NO_VALUE, ValueFormat, formatStayDate, formatStayDateLong, formatValue, stayDateMs } from './chartFormat';
import { Highcharts, HighchartsReact, chartTheme } from './chartEngine';
import { CHART_COLORS, CHART_HEIGHT, CHART_MARKS, ChartRole, ChartSize, colorOfRole } from './chartTokens';
import { bandKey, dashedLineKey, lineKey, tooltipCard, tooltipRow } from './chartTooltip';
import ChartMessage from './ChartMessage';

// Lines over stay dates, one value per date: the rate of your hotel against the compset median,
// an occupancy, a price per channel.
// The role of a line selects its colour: 'own' is blue-600, 'compset' is gray-500, and each
// 'series' line takes the next series slot (orange-600, cyan-600, …). Blue always means your hotel.
// An optional band shows the min–max range of the compset behind the lines, as a light gray wash.
// A null value is a gap in the line. A value with no neighbour is a dot.
// Two or more lines (or a line and the band) get a legend above the plot. A pointer, or the arrow
// keys after Tab, on a date shows one tooltip with every value of that date.
// With no value at all, the chart shows emptyLabel in a box of the same height.
// A dashed line is a second cue beside the colour: a remainder ("Other") or a comparison ("Last year"). The legend and
// the tooltip key show the dash too.
// With onSeriesClick, a click on a line or on its legend item calls it with the id of the line (to focus one line), and
// the legend item does not hide the line. The legend items take the Tab key, so the keyboard can do it too. A click on
// the band legend still hides the band.
// tooltipPrefix puts a word before the date of the tooltip ("As of Wed 21 Oct 2026": the value as it stood that day).
// tooltipOrder 'value' lists the lines of the tooltip largest first (a line with no value last, the band after the lines);
// the default 'series' keeps the order of the legend.
// The chart knows nothing about the data: the caller gives the dates, the values and the labels.

export type LineChartSeries = {
	id: string,
	label: string,
	// One value per date. null = no value (a gap).
	values: (number | null)[],
	// Default 'series'.
	role?: ChartRole,
	// A dashed line ("Other", "Last year"). Default false.
	dashed?: boolean,
};

export type LineChartBand = {
	label: string,
	lo: (number | null)[],
	hi: (number | null)[],
};

type Props = {
	// ISO dates (YYYY-MM-DD), in ascending order. Each values row has one value per date.
	dates: string[],
	series: LineChartSeries[],
	band?: LineChartBand,
	// money: €142 · percent: a fraction, 0.714 → 71% · number: 1,284
	format: ValueFormat,
	// An ISO 4217 code for 'money'. Default 'EUR'.
	currency?: string,
	// The decimals of the values in the tooltip. The axis shows no decimals. Default 0.
	decimals?: number,
	// The total height: small 200 px, normal 280 px, large 360 px.
	size?: ChartSize,
	// The name of the chart for a screen reader.
	ariaLabel: string,
	// The text when no line has a value.
	emptyLabel?: string,
	// 'short': each date label of the axis ends with the short year of its tick, "4 Jan ’27", for dates that
	// cross a year end. The labels are never cut or shortened, and the plot gets 16 px on the left for the
	// first label. Default: no year ("4 Jan").
	axisYear?: 'short',
	// A click on a line or on its legend item, with the id of the line. The legend item then does not hide the line.
	onSeriesClick?: (id: string) => void,
	// A word before the date in the tooltip title, for example "As of". Default: the date only.
	tooltipPrefix?: string,
	// 'value': the tooltip lists the lines largest first. Default 'series': the order of the legend.
	tooltipOrder?: 'series' | 'value',
};

const BAND_ID = '__band';

const isoOfMs = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const hasValue = (v: number | null | undefined): v is number => v !== null && v !== undefined && !Number.isNaN(v);

// A value with no value before it and no value after it. The line cannot show it, so it gets a dot.
const isIsolated = (values: (number | null)[], i: number): boolean =>
	hasValue(values[i]) && !hasValue(values[i - 1]) && !hasValue(values[i + 1]);

const LineChart: React.FunctionComponent<Props> = ({
	dates, series, band, format, currency, decimals = 0, size = 'normal', ariaLabel, emptyLabel = 'No data for this period.', axisYear,
	onSeriesClick, tooltipPrefix, tooltipOrder = 'series',
}) => {
	// The latest callback: a new function on each render of the caller does not build the chart again.
	const clickRef = useRef(onSeriesClick);
	clickRef.current = onSeriesClick;
	const clickable = onSeriesClick !== undefined;
	const height = CHART_HEIGHT[size];
	const isEmpty = series.every(s => !s.values.some(hasValue))
		&& (band === undefined || !band.lo.some(hasValue));

	const options = useMemo<Highcharts.Options>(() => {
		const xs = dates.map(stayDateMs);
		const indexOfMs = new Map(xs.map((ms, i) => [ms, i]));
		const valueOptions: FormatOptions = { currency, decimals };
		const axisOptions: FormatOptions = { currency, decimals: 0 };

		let seriesIndex = 0;
		const colors = series.map(s => {
			const role = s.role ?? 'series';
			const color = colorOfRole(role, seriesIndex);
			if (role === 'series') seriesIndex += 1;
			return color;
		});

		const lines: Highcharts.SeriesOptionsType[] = series.map((s, i) => ({
			type: 'line',
			id: s.id,
			name: s.label,
			color: colors[i],
			zIndex: s.role === 'own' ? 3 : 2,
			legendIndex: i,
			...(s.dashed === true ? { dashStyle: 'ShortDash' as const } : {}),
			data: s.values.map((v, j) => isIsolated(s.values, j)
				? { x: xs[j], y: v, marker: { enabled: true } }
				: { x: xs[j], y: hasValue(v) ? v : null }),
		}));

		const compsetAt = series.findIndex(s => s.role === 'compset');
		const bandIndex = compsetAt >= 0 ? compsetAt : series.length - 1;
		const all: Highcharts.SeriesOptionsType[] = band === undefined ? lines : [...lines, {
			type: 'arearange',
			id: BAND_ID,
			name: band.label,
			color: CHART_COLORS.band,
			fillOpacity: CHART_MARKS.bandOpacity,
			lineWidth: 0,
			zIndex: 0,
			legendIndex: bandIndex + 0.5,
			marker: { enabled: false, states: { hover: { enabled: false } } },
			data: xs.map((x, j) => {
				const lo = band.lo[j];
				const hi = band.hi[j];
				return hasValue(lo) && hasValue(hi) ? [x, lo, hi] : [x, null, null];
			}),
		}];

		const tooltipRows = (i: number, isVisible: (id: string) => boolean): string[] => {
			if (tooltipOrder === 'value') {
				// The lines by value, largest first; a line with no value last; the band after the lines.
				const lineRows = series.map((s, k) => ({ s, k })).filter(({ s }) => isVisible(s.id))
					.sort((a, b) => {
						const va = hasValue(a.s.values[i]) ? a.s.values[i] as number : -Infinity;
						const vb = hasValue(b.s.values[i]) ? b.s.values[i] as number : -Infinity;
						return va === vb ? a.k - b.k : (vb > va ? 1 : -1);
					})
					.map(({ s, k }) => tooltipRow(s.dashed === true ? dashedLineKey(colors[k]) : lineKey(colors[k]), formatValue(s.values[i], format, valueOptions), s.label));
				if (band === undefined || !isVisible(BAND_ID)) return lineRows;
				const lo = band.lo[i];
				const hi = band.hi[i];
				const text = hasValue(lo) && hasValue(hi) ? `${formatValue(lo, format, valueOptions)} – ${formatValue(hi, format, valueOptions)}` : NO_VALUE;
				return [...lineRows, tooltipRow(bandKey(), text, band.label)];
			}
			const rows: string[] = [];
			series.forEach((s, k) => {
				if (isVisible(s.id)) rows.push(tooltipRow(s.dashed === true ? dashedLineKey(colors[k]) : lineKey(colors[k]), formatValue(s.values[i], format, valueOptions), s.label));
				if (band !== undefined && k === bandIndex && isVisible(BAND_ID)) {
					const lo = band.lo[i];
					const hi = band.hi[i];
					const text = hasValue(lo) && hasValue(hi)
						? `${formatValue(lo, format, valueOptions)} – ${formatValue(hi, format, valueOptions)}`
						: NO_VALUE;
					rows.push(tooltipRow(bandKey(), text, band.label));
				}
			});
			return rows;
		};

		const withYear = axisYear === 'short';
		const own: Highcharts.Options = {
			chart: {
				...(withYear ? { spacingLeft: 16 } : {}),
				...(clickable ? {
					events: {
						// A legend item selects; it does not switch the line on and off. So its button is not a toggle.
						render: function (this: Highcharts.Chart) {
							const root = (this as Highcharts.Chart & { renderTo: HTMLElement }).renderTo;
							window.setTimeout(() => root.querySelectorAll<HTMLButtonElement>('.highcharts-a11y-proxy-container-after button[aria-pressed]')
								.forEach(button => button.removeAttribute('aria-pressed')), 0);
						},
					},
				} : {}),
			},
			...(clickable ? { lang: { accessibility: { legend: { legendItem: 'Select {itemName}' } } } } : {}),
			legend: {
				enabled: all.length >= 2,
				...(clickable ? {
					events: {
						// A line focuses; the band keeps the default (it hides and shows).
						itemClick: function (event: Highcharts.LegendItemClickEventObject) {
							const id = (event.legendItem as Highcharts.Series).options?.id;
							if (id === undefined || id === BAND_ID) return;
							event.preventDefault();
							clickRef.current?.(id);
						},
					},
				} : {}),
			},
			...(clickable ? {
				plotOptions: {
					line: {
						cursor: 'pointer',
						events: {
							click: function (this: Highcharts.Series) {
								if (this.options.id !== undefined) clickRef.current?.(this.options.id);
							},
						},
					},
				},
			} : {}),
			xAxis: {
				type: 'datetime',
				labels: {
					...(withYear ? { style: { textOverflow: 'none', whiteSpace: 'nowrap' } } : {}),
					// Highcharts selects the ticks, so the year comes from the tick value, not from `dates`.
					formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
						const iso = isoOfMs(Number(this.value));
						return withYear ? `${formatStayDate(iso)} ’${iso.slice(2, 4)}` : formatStayDate(iso);
					},
				},
			},
			yAxis: {
				labels: {
					formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
						return formatValue(Number(this.value), format, axisOptions);
					},
				},
			},
			tooltip: {
				formatter: function (this: Highcharts.Point) {
					const i = indexOfMs.get(Number(this.x));
					if (i === undefined) return false;
					const chart = this.series.chart;
					const isVisible = (id: string): boolean => chart.series.find(s => s.options.id === id)?.visible ?? true;
					const rows = tooltipRows(i, isVisible);
					if (rows.length === 0) return false;
					return tooltipCard(tooltipPrefix === undefined ? formatStayDateLong(dates[i]) : `${tooltipPrefix} ${formatStayDateLong(dates[i])}`, rows);
				},
			},
			accessibility: {
				description: ariaLabel,
				// A screen reader reads a point with the formats of the tooltip: "Mon 7 Dec 2026, €109. Your hotel."
				point: {
					descriptionFormatter: (point: Highcharts.Point): string => {
						const i = indexOfMs.get(Number(point.x));
						if (i === undefined) return '';
						const range = point as Highcharts.Point & { low?: number, high?: number };
						const value = range.low !== undefined && range.high !== undefined
							? `${formatValue(range.low, format, valueOptions)} – ${formatValue(range.high, format, valueOptions)}`
							: formatValue(point.y, format, valueOptions);
						return `${formatStayDateLong(dates[i])}, ${value}. ${point.series.name}.`;
					},
				},
			},
			series: all,
		};
		return Highcharts.merge(chartTheme(height), own);
	}, [dates, series, band, format, currency, decimals, height, ariaLabel, axisYear, clickable, tooltipPrefix, tooltipOrder]);

	if (isEmpty) return <ChartMessage height={height} ariaLabel={ariaLabel}>{emptyLabel}</ChartMessage>;

	return <div role="figure" aria-label={ariaLabel} className="w-full">
		<HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: '100%' } }} />
	</div>;
};

export default LineChart;
